import { useContext, useEffect } from 'react';
import { QueryObserverOptions, useQuery } from 'react-query';
import AppContext from '../contexts/AppContext';
import inspectionTemplateValidationApi, {
  InspectionTemplateValidation,
} from '../utils/api/inspectionTemplateValidation';

function getQueryKey(projectId: number, uploadGuid: string) {
  return ['project', projectId, 'upload', uploadGuid, 'template-validation'];
}

/**
 * Hook to fetch and validate inspection template for a specific map/upload
 * Determines if the map is using primary or secondary template
 * 
 * @param projectId - The project ID
 * @param uploadGuid - The upload GUID (map ID)
 * @param queryOptions - Additional React Query options
 * @returns React Query result with template validation data
 * 
 * @example
 * const { data, isLoading, error } = useInspectionTemplateValidation(projectId, mapId);
 * 
 * if (data?.is_primary) {
 *   // Map is using primary template
 * } else {
 *   // Map is using secondary template
 * }
 */
export function useInspectionTemplateValidation(
  projectId: number,
  uploadGuid: string,
  queryOptions: Omit<
    QueryObserverOptions<InspectionTemplateValidation>,
    'queryKey' | 'queryFn' | 'enabled' | 'keepPreviousData'
  > = {},
) {
  const appContext = useContext(AppContext);

  const query = useQuery<InspectionTemplateValidation>({
    queryKey: getQueryKey(projectId, uploadGuid),
    queryFn: () =>
      appContext.loadData(async () => {
        if (!projectId || !uploadGuid) {
          throw new Error('Missing projectId or uploadGuid');
        }

        return await inspectionTemplateValidationApi.validate(
          projectId,
          uploadGuid,
        );
      }),
    enabled: false,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    ...queryOptions,
  });

  useEffect(() => {
    if (projectId && uploadGuid) {
      query.refetch();
    }
  }, [projectId, uploadGuid]);

  return query;
}

/**
 * Helper function to check if a map is using the primary template
 * @param validation - The validation result from the hook
 * @returns boolean indicating if primary template is used
 */
export function isPrimaryTemplate(
  validation: InspectionTemplateValidation | undefined,
): boolean {
  return validation?.is_primary === true;
}

/**
 * Helper function to check if a map is using the secondary template
 * @param validation - The validation result from the hook
 * @returns boolean indicating if secondary template is used
 */
export function isSecondaryTemplate(
  validation: InspectionTemplateValidation | undefined,
): boolean {
  return validation?.is_primary === false;
}
